import { gridAxes, hasBlock } from "@gen/logic-grid/axes.ts";
import type { Puzzle } from "@gen/logic-grid/types.ts";
import { type Cell, cellKey, cellName, type Mark, type Marks } from "../lib/marks.ts";

/**
 * The grid, drawn as a staircase and marked by tapping.
 *
 * The shape is the real one and matches what is printed: with K categories the
 * first runs down the side against all the others, and each later one steps in
 * underneath against only the ones before it — so a cell exists where its
 * column category outranks its row category, and the bottom-right of the square
 * is not a block at all. src/logic-grid/pdf/grid.ts decides the same shape for
 * print, and components/GridEmblem.tsx draws it as ornament.
 *
 * Column headings are set vertically. A category value is a word, not a letter,
 * and eight of them across the top of a phone is the whole reason logic grids
 * are printed landscape.
 *
 * Two things keep it usable when it does not fit. It escapes the page column,
 * because the column is capped at 62rem and an Expert staircase is 1102px wide,
 * so no amount of zooming out would ever have shown the whole thing. And the row
 * labels are sticky, because scrolling a grid whose row names have gone off the
 * left is how a reader marks the wrong row. A sticky offset has to be a number,
 * which is why the category column is pinned to a fixed 1.5rem: the value column
 * pins against it.
 *
 * No z-index on either. Sticky is a positioned element and the cells it slides
 * over are not, so it already wins; naming a number only entered it into a
 * contest with the site header, which it also won, and slid over that too.
 */
export default function PuzzleGrid({
  puzzle,
  marks,
  wrong,
  highlight,
  onToggle,
}: {
  puzzle: Puzzle;
  marks: Marks;
  /** Cell keys the reader has been told are wrong, if they asked. */
  wrong?: Set<string>;
  /** One cell to point at, when a hint has named it. */
  highlight?: Cell | null;
  onToggle: (cell: Cell) => void;
}) {
  const K = puzzle.categories.length;
  const M = puzzle.size;
  /*
   * The same arrangement the interior prints — see @gen/logic-grid/axes.ts.
   * This used to compute its own, mirrored, which meant a reader solving on
   * screen with the book open had the categories in the opposite order.
   */
  const { rowCats, colCats } = gridAxes(K);
  const highlightKey = highlight ? cellKey(highlight) : null;

  return (
    /*
     * Full width of the window, not of the page column. `mx-[calc(50%-50vw)]`
     * puts the left edge at the window's left edge from wherever the column
     * happens to start; the table inside stays centred, so a grid that does fit
     * looks exactly as it did.
     *
     * Sideways only, and no height cap. Capping the height and scrolling both
     * ways made this a vertical scroll container sitting in the middle of the
     * page, so a wheel or a trackpad over the grid did something different from
     * the same gesture an inch to the left, and once the grid had reached its
     * own bottom, sometimes nothing at all. A reader has no way to know they
     * are inside a second scrolling thing, and should not have to.
     */
    <div className="overflow-x-auto mx-[calc(50%-50vw)] w-[100vw] px-[clamp(1rem,5vw,2rem)] py-2">
      <table className="border-collapse mx-auto">
        <thead>
          <tr>
            <th className="p-0 bg-parchment" colSpan={2} />
            {colCats.map((cc) => (
              <th
                key={cc}
                colSpan={M}
                className="text-[0.62rem] uppercase tracking-[0.12em] font-bold text-rust pb-1 px-1"
              >
                {puzzle.categories[cc]?.name}
              </th>
            ))}
          </tr>
          <tr>
            <th className="p-0 bg-parchment" colSpan={2} />
            {colCats.flatMap((cc) =>
              puzzle.categories[cc]?.values.map((value, cv) => (
                <th
                  key={`${cc}-${cv}`}
                  className="h-[7.5rem] align-bottom pb-[0.35rem] px-0 font-normal text-label text-muted"
                >
                  <span className="[writing-mode:vertical-rl] rotate-180 whitespace-nowrap">
                    {value}
                  </span>
                </th>
              )),
            )}
          </tr>
        </thead>
        <tbody>
          {rowCats.flatMap((rc) =>
            (puzzle.categories[rc]?.values ?? []).map((rowValue, rv) => (
              <tr key={`${rc}-${rv}`}>
                {rv === 0 ? (
                  <th
                    rowSpan={M}
                    className="text-[0.62rem] uppercase tracking-[0.12em] font-bold text-rust px-1 w-[1.5rem] min-w-[1.5rem] sticky left-0 bg-parchment"
                  >
                    <span className="[writing-mode:vertical-rl] rotate-180 whitespace-nowrap">
                      {puzzle.categories[rc]?.name}
                    </span>
                  </th>
                ) : null}
                <th className="text-right whitespace-nowrap font-normal text-label text-muted pr-2 pl-1 sticky left-[1.5rem] bg-parchment">
                  {rowValue}
                </th>
                {colCats.flatMap((cc) =>
                  Array.from({ length: M }, (_, cv) => {
                    if (!hasBlock(rc, cc)) {
                      return <td key={`${cc}-${cv}`} className="border-0 bg-transparent" />;
                    }
                    /*
                     * A cell's name is the lower category first — marks.ts keys
                     * on `i:a:j:b` with i < j so one square has exactly one
                     * name. Which category is drawn on which axis is a layout
                     * question and must not reach the key, or the same square
                     * gets two names depending on where it was drawn.
                     */
                    const cell: Cell =
                      rc < cc
                        ? { rowCat: rc, rowVal: rv, colCat: cc, colVal: cv }
                        : { rowCat: cc, rowVal: cv, colCat: rc, colVal: rv };
                    const key = cellKey(cell);
                    return (
                      <GridCell
                        key={`${cc}-${cv}`}
                        puzzle={puzzle}
                        cell={cell}
                        mark={marks[key]}
                        isWrong={wrong?.has(key) ?? false}
                        isHighlight={key === highlightKey}
                        edge={{
                          left: cv === 0,
                          right: cv === M - 1,
                          top: rv === 0,
                          bottom: rv === M - 1,
                        }}
                        onToggle={onToggle}
                      />
                    );
                  }),
                )}
              </tr>
            )),
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One block of the staircase, on its own, at a size a thumb can hit.
 *
 * The staircase is the right picture of a logic puzzle and the wrong thing to
 * put on a phone: an Expert grid is 1102px wide, so two thirds of it sits off
 * the screen no matter what. But a reader on a phone is not solving here. They
 * have the book open, they are copying in what they already know, and they want
 * a hint — which is a block at a time, in order, and that fits comfortably.
 *
 * Values stay on their side across the top, as they are in the staircase and in
 * the book. Laying them flat here does fit, and it makes this view a different
 * picture of the same puzzle from the one the reader has on the page in front
 * of them, which is a worse trade than a tilted head.
 */
export function PuzzleBlock({
  puzzle,
  block,
  marks,
  wrong,
  highlight,
  onToggle,
}: {
  puzzle: Puzzle;
  /** Which pair of categories, lower first — the same order marks.ts keys on. */
  block: [number, number];
  marks: Marks;
  wrong?: Set<string>;
  highlight?: Cell | null;
  onToggle: (cell: Cell) => void;
}) {
  const M = puzzle.size;
  const [rowCat, colCat] = block;
  const row = puzzle.categories[rowCat];
  const col = puzzle.categories[colCat];
  const highlightKey = highlight ? cellKey(highlight) : null;

  return (
    <div className="overflow-x-auto -mx-[clamp(1rem,5vw,2rem)] px-[clamp(1rem,5vw,2rem)] py-2">
      <table className="border-collapse mx-auto">
        <caption className="caption-top text-[0.62rem] uppercase tracking-[0.12em] font-bold text-rust pb-2">
          {row?.name} × {col?.name}
        </caption>
        <thead>
          <tr>
            <th className="p-0" />
            {col?.values.map((value, cv) => (
              <th
                key={cv}
                className="h-[6rem] align-bottom pb-[0.35rem] px-0 font-normal text-label text-muted"
              >
                <span className="[writing-mode:vertical-rl] rotate-180 whitespace-nowrap">
                  {value}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {row?.values.map((rowValue, rv) => (
            <tr key={rv}>
              <th className="text-right whitespace-nowrap font-normal text-[0.8rem] text-muted pr-2">
                {rowValue}
              </th>
              {Array.from({ length: M }, (_, cv) => {
                const cell: Cell = { rowCat, rowVal: rv, colCat, colVal: cv };
                const key = cellKey(cell);
                return (
                  <GridCell
                    key={cv}
                    puzzle={puzzle}
                    cell={cell}
                    mark={marks[key]}
                    isWrong={wrong?.has(key) ?? false}
                    isHighlight={key === highlightKey}
                    edge={{
                      left: cv === 0,
                      right: cv === M - 1,
                      top: rv === 0,
                      bottom: rv === M - 1,
                    }}
                    onToggle={onToggle}
                    big
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One cell.
 *
 * A real `<button>` inside the `<td>` rather than a click handler on the cell,
 * so the whole grid is reachable and operable from a keyboard without any
 * bespoke key handling — and so each cell can carry its own accessible name.
 * A grid of divs would need every bit of that written by hand.
 */
function GridCell({
  puzzle,
  cell,
  mark,
  isWrong,
  isHighlight,
  edge,
  onToggle,
  big = false,
}: {
  puzzle: Puzzle;
  cell: Cell;
  mark: Mark | undefined;
  isWrong: boolean;
  isHighlight: boolean;
  edge: { left: boolean; right: boolean; top: boolean; bottom: boolean };
  onToggle: (cell: Cell) => void;
  /** One block on its own has room for a proper touch target. */
  big?: boolean;
}) {
  const borders = [
    edge.left ? "border-l-2 border-l-line" : "",
    edge.right ? "border-r-2 border-r-line" : "",
    edge.top ? "border-t-2 border-t-line" : "",
    edge.bottom ? "border-b-2 border-b-line" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const state = isWrong
    ? "bg-rust text-panel"
    : isHighlight
      ? "bg-faint outline-2 outline-rust outline-offset-[-2px]"
      : "bg-panel";

  const label = `${cellName(puzzle, cell)}: ${mark ?? "not marked"}${isWrong ? ", wrong" : ""}`;
  const size = big ? "w-[2.75rem] h-[2.75rem] text-[1.3rem]" : "w-[2rem] h-[2rem] text-[1rem]";

  return (
    <td className={`border border-faint p-0 ${borders}`}>
      <button
        type="button"
        aria-label={label}
        aria-pressed={mark !== undefined}
        onClick={() => onToggle(cell)}
        className={`${size} min-h-0 flex items-center justify-center cursor-pointer border-0 leading-none select-none hover:bg-faint focus-visible:outline-2 focus-visible:outline-rust ${state} ${
          mark === "tick" ? "text-rust font-bold" : mark === "cross" ? "text-muted" : ""
        } ${isWrong ? "hover:bg-rust" : ""}`}
      >
        {mark === "tick" ? "✓" : mark === "cross" ? "✕" : ""}
      </button>
    </td>
  );
}
